import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import store from "src/page/store";
import { immediateToast } from "izitoast-react";
import { refreshSession } from "src/utils";
import { getConfig, AppConfig } from "src/config/Config";
import { logoutAction } from "src/page/auth/loginSlice";
import { showErrorAlert, errorMapping } from "src/utils/errors";
import {
  trackEvent,
  API_ERROR_EVENTS,
  parseGraphQLOperation,
  trackGraphQLOperation,
  enrichEventProperties
} from "src/services/analytics";

// Error types that still propagate (callers/RTK see them) but must NOT raise a
// user-facing toast — internal preconditions the UI already handles. e.g. Xero
// metadata fetched while the connection isn't ACTIVE (see useXeroMeta gating).
const SILENT_ERROR_TYPES = new Set<string>(["ACCOUNTING_CONNECTION_NOT_ACTIVE"]);

let apiInstance: AxiosInstance | null = null;

let isRefreshing = false;
let refreshPromise: Promise<any> | null = null;
let isLoggedOut = false;
// Dedupe the access-denied alert: a single page often fires several denied ops
// at once, and we only want to surface one "access denied" prompt per burst.
let lastAccessDeniedAt = 0;

export function resetLoggedOutState() {
  isLoggedOut = false;
  isRefreshing = false;
  refreshPromise = null;
}

export function getApiClient(): AxiosInstance {
  if (apiInstance) {
    return apiInstance;
  }

  const config: AppConfig = getConfig();

  /**
 * A 401 while our own id token is still valid is an authorization DENY from
 * the AppSync authorizer (AVP), not an expired session. Refreshing the token
 * cannot change that answer, and treating it as expiry sent the hub into a
 * refresh/retry spiral that ended in a forced logout and hundreds of requests
 * (QA OBS-QB1, 2026-09-24: a Team Member approver opening a queued item).
 */
const idTokenStillValid = (token?: string | null): boolean => {
  try {
    const payload = JSON.parse(atob(String(token).split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));

    return Number(payload.exp) * 1000 > Date.now() + 5_000;
  } catch {
    return false;
  }
};

const api = axios.create({
    baseURL: config.API_URL,
    headers: {
      "Content-Type": "application/json",
      partner_id: config.PARTNER_ID
    }
  });

  api.interceptors.request.use(async (configuration: InternalAxiosRequestConfig) => {
    if (isLoggedOut) {
      return Promise.reject(new Error("Session expired. User logged out."));
    }
    try {
      const {
        auth: { cognitoIdToken }
      } = store.getState();

      if (cognitoIdToken) {
        configuration.headers = configuration.headers || {};
        configuration.headers["Authorization"] = `Bearer ${cognitoIdToken}`;
      }
    } catch (error) {
      console.error(error);
    }
    return configuration;
  });

  api.interceptors.response.use(
    (response: AxiosResponse) => {
      if (response.config.url?.includes("/graphql") && response.config.data) {
        try {
          const requestData =
            typeof response.config.data === "string" ? JSON.parse(response.config.data) : response.config.data;

          if (requestData?.query) {
            const { methodName, methodType } = parseGraphQLOperation(requestData.query);
            const hasErrors = !!response.data.errors;

            trackGraphQLOperation({
              methodName,
              methodType,
              success: !hasErrors,
              requestPayload: requestData.variables,
              responseData: response.data,
              ...(hasErrors && {
                errorMessage: response.data.errors[0]?.message,
                errorType: response.data.errors[0]?.errorType
              })
            });
          }
        } catch (error) {
          console.error("Failed to track GraphQL operation:", error);
        }
      }

      if (response.data.errors) {
        const error = response.data.errors[0];
        const errorType = error.errorType;
        // Background lookups (e.g. per-row approver/name resolution) opt out of the
        // global toast; they handle their own failure and the error still rejects.
        const suppressErrorToast = (response.config as { suppressErrorToast?: boolean })?.suppressErrorToast;

        if (suppressErrorToast) {
          // fall through to the throw below without alerting
        } else if (errorMapping[errorType]) {
          showErrorAlert(errorType, error.message);
        } else if (!SILENT_ERROR_TYPES.has(errorType)) {
          const errorMessage = error.message;
          let messageObj: { message?: string } | undefined;

          try {
            messageObj = JSON.parse(errorMessage);
          } catch (err) {
            console.log(err);
            messageObj = undefined;
          }

          immediateToast("error", {
            title: "Error",
            message: messageObj?.message ?? errorMessage,
            timeout: 3000,
            position: "topCenter"
          });
        }

        throw new Error(error.message);
      }
      return response.data;
    },
    async function (error: any) {
      if (error.config?.url?.includes("/graphql") && error.config.data) {
        try {
          const requestData = typeof error.config.data === "string" ? JSON.parse(error.config.data) : error.config.data;

          if (requestData?.query) {
            const { methodName, methodType } = parseGraphQLOperation(requestData.query);
            const errorMessage = error?.response?.data?.errors?.[0]?.message || error.message || "Unknown error";
            const errorType = error?.response?.data?.errors?.[0]?.errorType || error.constructor.name;

            trackGraphQLOperation({
              methodName,
              methodType,
              success: false,
              errorMessage,
              errorType,
              requestPayload: requestData.variables,
              responseData: error?.response?.data
            });
          }
        } catch (trackingError) {
          console.error("Failed to track GraphQL error:", trackingError);
        }
      }

      const originalRequest: InternalAxiosRequestConfig = error.config;
      const {
        auth: { cognitoRefreshToken }
      } = store.getState();
      const dispatch = store.dispatch;

      if (error.response?.status === 401) {
        if (isLoggedOut) {
          return Promise.reject(new Error("Session expired. User logged out."));
        }
        // Best-effort calls (e.g. the allocation sidebar's optional wallet
        // coding-defaults, which authorizer-DENY for roles that can't read
        // wallets — CPC has no user↔wallet edges) opt out of the retry/refresh,
        // the global "no permission" toast, and logout. The caller handles the
        // empty result.
        if ((originalRequest as { suppressAuthError?: boolean })?.suppressAuthError) {
          return Promise.reject(new Error("Access denied"));
        }
        const {
          auth: { cognitoIdToken }
        } = store.getState();
        const firstErrorType = error.response?.data?.errors?.[0]?.errorType;

        // Valid token + UnauthorizedException = the authorizer denied this
        // operation. Say so and stop: no refresh, no retry, no logout.
        if (firstErrorType === "UnauthorizedException" && idTokenStillValid(cognitoIdToken)) {
          const now = Date.now();

          if (now - lastAccessDeniedAt > 2000) {
            lastAccessDeniedAt = now;
            showErrorAlert("UnauthorizedException", "You don't have permission to do this.");
          }
          return Promise.reject(new Error("Access denied"));
        }
        const originalRequestWithRetry = originalRequest as InternalAxiosRequestConfig & { _retry?: number };

        if (!originalRequestWithRetry._retry) originalRequestWithRetry._retry = 0;

        if (originalRequestWithRetry._retry < 1) {
          originalRequestWithRetry._retry += 1;

          // Track retry attempt
          const retryProperties = enrichEventProperties({
            endpoint: originalRequest.url || "unknown",
            method: originalRequest.method?.toUpperCase() || "unknown",
            retry_count: originalRequestWithRetry._retry
          });

          trackEvent(API_ERROR_EVENTS.API_RETRY_ATTEMPTED, retryProperties);

          if (!isRefreshing) {
            isRefreshing = true;
            refreshPromise = (async () => {
              try {
                const session = await refreshSession(cognitoRefreshToken ?? undefined);

                // refreshSession swallows its own errors and returns undefined;
                // that must end the session, not leave every later 401 retrying.
                if (!session || !(session as { idToken?: string }).idToken) {
                  throw new Error("Token refresh returned no session");
                }

                isRefreshing = false;
                refreshPromise = null;
                return session;
              } catch (err) {
                isRefreshing = false;
                refreshPromise = null;
                isLoggedOut = true;
                // Track token refresh failure
                const refreshErrorProperties = enrichEventProperties({
                  error_message: err instanceof Error ? err.message : "Token refresh failed"
                });

                trackEvent(API_ERROR_EVENTS.TOKEN_REFRESH_FAILED, refreshErrorProperties);
                dispatch(logoutAction());
                return Promise.reject(new Error("Session expired. User logged out."));
              }
            })();
          }

          try {
            const session = await refreshPromise;

            if (originalRequestWithRetry.headers) {
              originalRequestWithRetry.headers["Authorization"] =
                `Bearer ${(session as unknown as { idToken: string }).idToken}`;
            }
            return api(originalRequestWithRetry);
          } catch (err) {
            return Promise.reject(new Error("Session expired. User logged out."));
          }
        } else {
          // We already refreshed the token once and the request is STILL 401.
          // A fresh, valid session that keeps getting rejected is an authorization
          // DENY from the AppSync authorizer — not an expired session. Show an
          // "access denied" prompt and DO NOT log the user out; only a genuine
          // session failure (refresh threw above) reaches logout.
          const errorType = error.response?.data?.errors?.[0]?.errorType;

          if (errorType === "UnauthorizedException") {
            const now = Date.now();

            if (now - lastAccessDeniedAt > 2000) {
              lastAccessDeniedAt = now;
              showErrorAlert("UnauthorizedException", "You don't have permission to do this.");
            }
            return Promise.reject(new Error("Access denied"));
          }

          isLoggedOut = true;
          dispatch(logoutAction());
          return Promise.reject(new Error("Session expired. User logged out."));
        }
      } else {
        immediateToast("error", {
          title: "Error",
          message: error.message || "An unexpected error occurred",
          timeout: 3000,
          position: "topCenter"
        });
        return Promise.reject(error);
      }
    }
  );

  apiInstance = api;
  return apiInstance;
}

const client = (
  data: { query: string; variables?: any },
  config?: AxiosRequestConfig & { suppressAuthError?: boolean; suppressErrorToast?: boolean }
) => {
  return getApiClient().post("/graphql", data, config);
};

export default client;
